import { useEffect, useState } from 'react';
import { Button } from '@lightning/ui/button';

const BREATH_DURATION_MS = 8000;

export default function PausePage() {
  const [pauseState, setPauseState] = useState<'idle' | 'active' | 'ended'>('idle');
  const isPausing = pauseState === 'active';
  const hasCompletedBreath = pauseState === 'ended';

  useEffect(() => {
    if (!isPausing) return undefined;

    const breathTimer = window.setTimeout(() => {
      setPauseState('ended');
    }, BREATH_DURATION_MS);

    return () => window.clearTimeout(breathTimer);
  }, [isPausing]);

  return (
    <div
      className="absolute inset-0 flex items-center justify-center overflow-y-auto px-6 text-how-space-deep sm:px-8"
      style={{ background: 'linear-gradient(180deg, var(--how-paper-soft) 0%, var(--color-how-page-wash) 100%)' }}
    >
      <main className="mx-auto flex w-full max-w-3xl -translate-y-6 flex-col items-center justify-center py-5 text-center sm:-translate-y-12 sm:py-6">
        <header>
          <h1 className="font-body text-[2rem] font-medium leading-tight tracking-[-0.025em] sm:text-[2.35rem]">
            Pause
          </h1>
          <p
            key={hasCompletedBreath ? 'reflection' : 'invitation'}
            className={`mt-2 text-sm leading-6 text-how-text-secondary/65${
              hasCompletedBreath ? ' pause-copy-reveal' : ''
            }`}
          >
            {hasCompletedBreath
              ? 'What does this moment call for from you?'
              : 'Make room for choice.'}
          </p>
        </header>

        <div className="my-9 flex h-52 w-52 items-center justify-center sm:my-10 sm:h-56 sm:w-56" aria-hidden="true">
          <div className={`pause-orb${isPausing ? ' pause-orb--active' : ''}`}>
            <span className="pause-orb__light" />
          </div>
        </div>

        <Button
          type="button"
          size="lg"
          onClick={() => setPauseState((current) => current === 'active' ? 'ended' : 'active')}
          aria-pressed={isPausing}
          className={isPausing
            ? 'h-11 min-w-24 rounded-full border border-how-space-deep/15 bg-white/70 px-6 font-body text-sm font-normal text-how-space-deep shadow-none hover:bg-white'
            : 'h-11 min-w-24 rounded-full bg-how-space-deep px-6 font-body text-sm font-normal text-white shadow-none hover:bg-how-space-mid'}
        >
          {isPausing ? 'End' : hasCompletedBreath ? 'Again' : 'Begin'}
        </Button>

        <p className="sr-only" aria-live="polite">
          {pauseState === 'idle' ? '' : isPausing ? 'Pause started.' : 'One breath complete.'}
        </p>
      </main>
    </div>
  );
}
